import {
  Badge, Button, Card, ErrorState, Field, LARGEST_PAGE, LoadingState, Notice, PageHeader, Row, Stack, Textarea,
  complaintTone, isMessage, useApi, useI18n,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * One complaint, as the family who raised it reads it (§4.14).
 *
 * The timeline arrives already filtered: the API never sends a guardian an internal handling note, so there
 * is nothing here that decides what to hide. What this screen owes them instead is a plain account of where
 * their complaint has got to — received, being looked at, dealt with — and the answer when there is one.
 *
 * Who inside the nursery is handling it is deliberately absent. The family is told it is being dealt with,
 * which is what they asked; naming the handler would turn every complaint into a second one.
 */
export function Complaint({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const { complaintId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const id = Number(complaintId);
  const [reply, setReply] = useState('');

  const complaint = useQuery({
    queryKey: ['complaints', id],
    queryFn: () => api.complaints.get(id),
    enabled: Number.isFinite(id),
  });

  const timeline = useQuery({
    queryKey: ['complaints', id, 'timeline'],
    queryFn: () => api.complaints.timeline(id, { pageSize: LARGEST_PAGE }),
    enabled: Number.isFinite(id),
  });

  const send = useMutation({
    mutationFn: () => api.complaints.reply(id, reply.trim()),
    onSuccess: async () => {
      setReply('');
      await queryClient.invalidateQueries({ queryKey: ['complaints'] });
    },
  });

  const withdraw = useMutation({
    mutationFn: () => api.complaints.withdraw(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['complaints'] });
      navigate(`/child/${childId}/complaints`);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reply.trim()) send.mutate();
  };

  if (complaint.isPending) return <LoadingState />;
  if (complaint.isError) {
    return <ErrorState error={complaint.error} onRetry={() => void complaint.refetch()} />;
  }

  const it = complaint.data;
  const entries = (timeline.data?.items ?? []);

  return (
    <Stack>
      <PageHeader title={it.subject} lede={it.reference} />

      <Card>
        <Stack>
          <Row>
            <Badge tone={complaintTone(it.status)}>{t(`complaint.status.${it.status}`)}</Badge>
            <span className="dg-muted">{t(`complaint.category.${it.category}`)}</span>
            <span className="dg-muted">{formatDateTime(it.createdAt)}</span>
          </Row>

          <p>{it.body}</p>

          {/* The answer, given the most prominent place on the screen, because it is why they came back. */}
          {it.resolutionSummary != null && (
            <Notice tone="success">
              <strong>{t('complaint.event.Resolved')}</strong>
              <br />
              {it.resolutionSummary}
            </Notice>
          )}

          {it.status === 'Submitted' && (
            <Notice tone="info">{t('complaints.awaiting')}</Notice>
          )}
        </Stack>
      </Card>

      <Card title={t('complaints.timeline')}>
        {timeline.isPending && <LoadingState />}
        {timeline.isError && (
          <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
        )}

        <Stack>
          {entries.map((entry) => (
            <div key={entry.id} className="dg-stack">
              <Row>
                <strong>{t(`complaint.event.${entry.eventType}`, { name: entry.subjectName ?? '' })}</strong>
                <span className="dg-muted">{formatDateTime(entry.at)}</span>
              </Row>
              {isMessage(entry) && entry.body != null && <p>{entry.body}</p>}
            </div>
          ))}
        </Stack>
      </Card>

      {it.canReply && (
        <Card title={t('complaints.reply')}>
          <form onSubmit={submit}>
            <Stack>
              {send.isError && <ErrorState error={send.error} />}

              {/* Replying to a complaint the nursery considers dealt with puts it back on their queue
                  (FR-CMP-11), and the parent is told so rather than left wondering. */}
              {it.status === 'Resolved' && <Notice tone="info">{t('complaints.reopens')}</Notice>}

              <Field label={t('complaints.reply')}>
                {(props) => (
                  <Textarea
                    {...props}
                    rows={4}
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                  />
                )}
              </Field>

              <Row>
                <Button type="submit" busy={send.isPending} busyLabel={t('action.saving')}>
                  {t('complaints.send')}
                </Button>

                <Button
                  type="button"
                  variant="danger"
                  onClick={() => withdraw.mutate()}
                  busy={withdraw.isPending}
                  busyLabel={t('action.saving')}
                >
                  {t('complaints.withdraw')}
                </Button>
              </Row>

              {withdraw.isError && <ErrorState error={withdraw.error} />}
            </Stack>
          </form>
        </Card>
      )}
    </Stack>
  );
}
